import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  Typography,
  Spin,
  Card,
  Row,
  Col,
  Button,
  Tag,
  Space,
  Popconfirm,
  message,
  Empty,
  Divider,
} from "antd";
import {
  ArrowLeftOutlined,
  DeleteOutlined,
  MailOutlined,
} from "@ant-design/icons";

import { REGION_LABELS, REGION_DESCRIPTIONS } from "../regions";

const { Title, Text } = Typography;

const formatDate = (value) => {
  if (!value) return "N/A";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "N/A";
  return d.toLocaleString();
};

function Field({ label, value, fullWidth, preserveWhitespace }) {
  const colProps = fullWidth ? { xs: 24 } : { xs: 24, sm: 12, md: 8, lg: 6 };

  const hasValue = value !== null && value !== undefined && value !== "";

  return (
    <Col {...colProps}>
      <div
        style={{
          fontSize: 12,
          fontWeight: 500,
          color: "#8c8c8c",
          textTransform: "uppercase",
          letterSpacing: 0.4,
          marginBottom: 6,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          color: "#262626",
          wordBreak: "break-word",
          whiteSpace: preserveWhitespace ? "pre-wrap" : "normal",
          lineHeight: 1.5,
        }}
      >
        {hasValue ? value : <span style={{ color: "#bfbfbf" }}>N/A</span>}
      </div>
    </Col>
  );
}

export default function SponsorshipDetails() {
  const [submission, setSubmission] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const location = useLocation();
  const history = useHistory();

  const id = location?.pathname?.split("/")[2];

  const fetchSubmissionById = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/sponsor/${id}`
      );
      const data = await response.json();

      if (data.success) {
        setSubmission(data.data);
      } else {
        setSubmission(null);
      }
    } catch {
      setSubmission(null);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchSubmissionById();
  }, [id]);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_MASTER}/sponsor/${id}`,
        { method: "DELETE" }
      );
      const data = await response.json();

      if (data.success) {
        message.success("Enquiry deleted");
        history.push("/sponsorships");
      } else {
        message.error(data.message || "Failed to delete enquiry");
      }
    } catch {
      message.error("Failed to delete enquiry");
    }
    setDeleting(false);
  };

  const region = submission?.region;

  return (
    <div style={{ padding: "20px", width: "100%" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          gap: 12,
          flexWrap: "wrap",
        }}
      >
        <Space size="middle" align="center">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => history.push("/sponsorships")}
          >
            Back
          </Button>
          <Title level={3} style={{ margin: 0 }}>
            Enquiry Details
          </Title>
        </Space>

        {submission && (
          <Popconfirm
            title="Delete this enquiry?"
            description="This action cannot be undone."
            okText="Delete"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
            onConfirm={handleDelete}
          >
            <Button danger icon={<DeleteOutlined />} loading={deleting}>
              Delete
            </Button>
          </Popconfirm>
        )}
      </div>

      {loading && (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Spin size="large" />
        </div>
      )}

      {!loading && !submission && (
        <Card>
          <Empty description="No sponsorship enquiry found" />
        </Card>
      )}

      {!loading && submission && (
        <Card
          bordered
          style={{
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)",
            borderRadius: 8,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              marginBottom: 12,
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {submission.organizationName ||
                  submission.contactName ||
                  "Unnamed Enquiry"}
              </Title>
              <Text type="secondary">
                Submitted
                {" "}
                {formatDate(submission.createdAt)}
              </Text>
            </div>
            {region && (
              <Tag
                color={region === "NIGERIA" ? "green" : "orange"}
                style={{ fontSize: 14, padding: "4px 10px" }}
              >
                {REGION_DESCRIPTIONS[region] || region}
              </Tag>
            )}
          </div>

          <Divider style={{ margin: "16px 0 24px" }} />

          <Row gutter={[24, 24]}>
            <Field label="Contact Name" value={submission.contactName} />
            <Field label="Organization" value={submission.organizationName} />
            <Field
              label="Email"
              value={
                submission.email ? (
                  <a href={`mailto:${submission.email}`}>
                    <MailOutlined style={{ marginRight: 6 }} />
                    {submission.email}
                  </a>
                ) : null
              }
            />
            <Field label="Projects To Sponsor" value={submission.projectCount} />
            <Field label="Estimated Total" value={submission.estimatedTotal} />
            <Field
              label="Where"
              value={region ? REGION_LABELS[region] || region : null}
            />
            <Field label="Submitted" value={formatDate(submission.createdAt)} />
            <Field
              label="Message"
              value={submission.message}
              fullWidth
              preserveWhitespace
            />
          </Row>
        </Card>
      )}
    </div>
  );
}
